import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { AFFILIATE_PATHS, CODE_PATTERN } from "../../affiliate/contract";
import { affiliateErrorText } from "./errorText";

/**
 * Replace the generated code — ONCE.
 *
 * `codeChangeable` is the SERVER's verdict (`affiliate_accounts.codeChangedAt is None`),
 * not a timestamp for this form to interpret: the input is disabled on that field alone, so
 * the browser and the API can never disagree about whether the change is still available.
 * The refusal is still handled — a second tab, a stale render, and `AFFILIATE_CODE_ALREADY_
 * CHANGED` comes back with a 409 that this form says out loud.
 *
 * The pattern is checked here too, against the SAME `CODE_PATTERN` the backend's
 * `is_valid_code` mirrors, so an obviously bad code is refused without a round trip — and
 * the sentence shown is the very one the server's `AFFILIATE_CODE_INVALID` maps to, rather
 * than a second wording that could drift from it.
 */
export default function CodeChangeForm({ account, t, tv, onAccountChanged }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const changeable = account.codeChangeable && account.status === "active";

  const submit = async (e) => {
    e.preventDefault();
    const next = code.trim();
    if (!CODE_PATTERN.test(next)) {
      setError(t("aff.error.AFFILIATE_CODE_INVALID"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put(AFFILIATE_PATHS.tenantCode, { code: next });
      // The 200 IS the account block — code, `codeChangeable` now false, status and the
      // rebuilt share links. Folding it in beats a second read: the links on screen must
      // never lag the code they encode.
      onAccountChanged(data);
      setCode("");
      toast.success(t("aff.code.saved"));
    } catch (err) {
      setError(affiliateErrorText(err, { t, tv, fallback: tv("errors.saveFailed") }));
    } finally {
      setBusy(false);
    }
  };

  if (!changeable) {
    return (
      <p className="mt-4 text-xs text-sol-ivory/50" data-testid="affiliate-code-locked">
        {account.status === "active" ? t("aff.code.locked") : t("aff.code.disabled")}
      </p>
    );
  }

  return (
    <form className="mt-4 border-t border-white/10 pt-4" onSubmit={submit} data-testid="affiliate-code-form">
      <label className="text-[11px] uppercase tracking-[0.16em] text-sol-gold/80" htmlFor="affiliate-code-input">
        {t("aff.code.title")}
      </label>
      <p className="text-xs text-sol-ivory/50 mt-1">{t("aff.code.hint")}</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          id="affiliate-code-input"
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          placeholder={t("aff.code.placeholder")}
          maxLength={16}
          disabled={busy}
          data-testid="affiliate-code-input"
          className="flex-1 min-w-0 bg-white/10 rounded-lg px-3 py-2 text-sm text-sol-ivory placeholder:text-sol-ivory/30"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="affiliate-code-submit"
          className="shrink-0 bg-sol-gold text-sol-midnight rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-50"
        >
          {t("aff.code.submit")}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-sol-ivory/40">{t("aff.code.pattern")}</p>
      {error && (
        <p className="mt-2 text-xs text-rose-300" role="alert" data-testid="affiliate-code-error">{error}</p>
      )}
    </form>
  );
}
